import { HardDrive, Laptop, type LucideIcon, Monitor, Server, Smartphone } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The marks: what an agent is, and what a device is.
 *
 * Every machine in this console used to be a `Cpu` outline, which is the icon a
 * generic admin panel uses for a generic machine. Two things are worth telling
 * apart here and neither of them is a CPU: an **agent** is Claude Code running
 * under somebody's own subscription, and a **device** is the laptop it is
 * running on. So the agent wears Claude's own burst and the device wears the
 * shape of the hardware its hostname says it is.
 */

/**
 * Claude's burst, drawn rather than fetched.
 *
 * Eleven tapered blades on a circle, wide at the rim and pinched at the centre.
 * The geometry is computed once at module load rather than written out as a
 * literal, because eleven hand-tuned quadrilaterals is eleven chances to typo a
 * coordinate and no chance at all of adjusting the weight later.
 *
 * `currentColor` by default, so it inherits whatever the row around it is: the
 * mark is an icon here, not a logo lockup, and a fixed orange in the middle of a
 * grey list is a sticker. Pass `brand` where it is genuinely standing in for
 * Claude itself.
 */
const BLADES = 11;
/** Half-width of a blade at the rim, in radians. Wider reads as a flower. */
const SPREAD = 0.13;

function bladePath(index: number): string {
  const angle = (index / BLADES) * Math.PI * 2 - Math.PI / 2;
  const inner = 1.6;
  const outer = 11.2;
  const point = (radius: number, offset: number) =>
    `${(12 + Math.cos(angle + offset) * radius).toFixed(2)} ${(12 + Math.sin(angle + offset) * radius).toFixed(2)}`;
  return [
    `M${point(inner, -SPREAD * 0.9)}`,
    `L${point(outer, -SPREAD)}`,
    `L${point(outer, SPREAD)}`,
    `L${point(inner, SPREAD * 0.9)}`,
    'Z',
  ].join('');
}

const BURST = Array.from({ length: BLADES }, (_, index) => bladePath(index)).join('');

/** Claude's own clay, for the places the mark stands in for Claude itself. */
export const CLAUDE_CLAY = '#d97757';

export function ClaudeMark({
  className,
  brand = false,
}: {
  className?: string;
  /** Draw it in Claude's clay rather than inheriting the row's colour. */
  brand?: boolean;
  /** Accepted and ignored: the mark is always hidden from the accessibility
      tree, and callers that draw lucide icons in the same slot pass it. */
  'aria-hidden'?: boolean;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={cn('size-4 shrink-0', className)}
      fill={brand ? CLAUDE_CLAY : 'currentColor'}
      aria-hidden
      focusable="false"
    >
      <title>Claude</title>
      <path d={BURST} />
    </svg>
  );
}

/**
 * The shape of a machine, guessed from what it calls itself.
 *
 * A guess, and cheap to be wrong about: every one of these is a laptop-shaped
 * thing on somebody's desk and the icon is recognition, not information. The
 * default is a laptop because that is what the product actually runs on.
 */
export function deviceIcon(hint: string | null | undefined): LucideIcon {
  const name = (hint ?? '').toLowerCase();
  if (/imac|mac-?mini|desktop|studio|tower/.test(name)) return Monitor;
  if (/server|runner|ec2|vm-|droplet|node-?\d/.test(name)) return Server;
  if (/nas|synology|storage/.test(name)) return HardDrive;
  if (/ipad|iphone|android|pixel/.test(name)) return Smartphone;
  return Laptop;
}

/**
 * A device's icon in a tinted well, at the size a row wants it.
 *
 * The well is what makes a 14px outline hold its own next to a 15px title. It
 * takes the presence colour, so the list can be read down its left edge: green
 * is working, grey is shut.
 */
export function DeviceMark({
  hint,
  tone = 'var(--dy-fg-3)',
  className,
}: {
  hint: string | null | undefined;
  /** A CSS colour: the device's presence, usually. */
  tone?: string;
  className?: string;
}) {
  const Icon = deviceIcon(hint);
  return (
    <span
      className={cn('grid size-8 shrink-0 place-items-center rounded-lg', className)}
      style={{
        background: `color-mix(in srgb, ${tone} 12%, transparent)`,
        color: tone,
      }}
      aria-hidden
    >
      <Icon className="size-4" />
    </span>
  );
}

/**
 * An agent's mark in a tinted well. Same idea as `DeviceMark`, and deliberately
 * the same size, because the two sit in the same lists and a person should read
 * "this is the machine, that is what is running on it" from the shape rather
 * than from the caption.
 */
export function AgentMark({
  tone = CLAUDE_CLAY,
  className,
}: {
  tone?: string;
  className?: string;
}) {
  return (
    <span
      className={cn('grid size-8 shrink-0 place-items-center rounded-lg', className)}
      style={{
        background: `color-mix(in srgb, ${tone} 14%, transparent)`,
        color: tone,
      }}
      aria-hidden
    >
      <ClaudeMark className="size-[15px]" />
    </span>
  );
}
